import './styles/globals.css'
import { AuthProvider, getAuthAppUrls } from '@mntn/auth-ui'
import { cn } from '@mntn/ui-components'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Link, Route, Routes, useLocation, useSearchParams } from 'react-router-dom'
import AdvertiserAccessGate from './components/advertiser-access-gate'
import { FlightDeckAudienceAlert } from './components/audience-alert/audience-alert'
import { DashboardWrapper } from './components/dashboard/dashboard-wrapper'
import { FlightDeckProvider } from './components/flight-deck-provider'
import { OrderCard } from './components/order-card/order-card'
import { MfeThemeWrapper } from './components/shared/mfe-theme-wrapper'
import { config } from './config'
import { DashboardSessionProvider, useDashboardSession } from './providers/dashboard-session/provider'
import { LoadingElement } from './providers/session/loading'
import type { AppSession } from './providers/session/session-provider'

// Mock props for Dashboard
const dashboardProps = {
  onCreateCampaignV2: (result: { campaignGroupId: number; productId: number }) => {
    window.alert(`Create Campaign V2 Mock:\n${JSON.stringify(result, null, 2)}`)
  },
  isLoading: false,
  onDuplicateCampaign: async (campaignId: number, isExperiment?: boolean) => {
    window.alert(`Duplicate campaign ${isExperiment ? 'as experiment' : ''} id: ${campaignId}`)
    return undefined
  },
  onArchiveCampaign: async (campaignId: number) => {
    const confirmed = window.confirm(
      `Are you sure you want to archive campaign ${campaignId}? This action cannot be reversed.`,
    )
    if (confirmed) {
      return { campaignGroupId: campaignId, refId: 1 }
    }
    return undefined
  },
}

const Navigation = () => {
  const location = useLocation()

  return (
    <nav className="p-4 bg-dark-bg border-b border-border mb-4">
      <div className="flex gap-4 items-center">
        <Link
          to="/"
          className={cn(
            'text-primary-accent no-underline font-semibold hover:text-primary-accent',
            location.pathname === '/' ? 'text-primary-accent' : 'text-secondary',
          )}
        >
          ← Dashboard
        </Link>
        <span className="text-primary">|</span>
        <Link
          to="/order-card"
          className={cn(
            'no-underline hover:text-primary-accent',
            location.pathname === '/order-card' ? 'text-primary-accent' : 'text-secondary',
          )}
        >
          Order Card
        </Link>
        <span className="text-primary">|</span>
        <Link
          to="/audience-alert"
          className={cn(
            'no-underline hover:text-primary-accent',
            location.pathname === '/audience-alert' ? 'text-primary-accent' : 'text-secondary',
          )}
        >
          Audience Alert
        </Link>
      </div>
    </nav>
  )
}

const App = () => {
  return (
    <AuthProvider
      passBaseUrl={config.VITE_MNTN_PASS_URL}
      apiBaseUrl={config.VITE_AUTH_API_BASE_URL}
      environment={config.VITE_AUTH_ENV}
    >
      <DashboardSessionProvider>
        <MfeThemeWrapper>
          <div className="bg-background-inverse">
            <AppWithAuth />
          </div>
        </MfeThemeWrapper>
      </DashboardSessionProvider>
    </AuthProvider>
  )
}

const AppWithAuth = () => {
  const { getToken, isSignedIn, isLoaded, session } = useDashboardSession()

  // Wait for auth to fully load
  if (!isLoaded) {
    return <LoadingElement />
  }

  // If not signed in, redirect to sign-in in standalone mode
  if (!isSignedIn) {
    if (config.VITE_FLIGHT_DECK_STANDALONE_MODE) {
      const signInUrl = getAuthAppUrls(config.VITE_MNTN_PASS_URL).signIn
      const returnUrl = encodeURIComponent(window.location.href)
      const redirectUrl = `${signInUrl}?redirect_url=${returnUrl}`

      window.location.href = redirectUrl
      return <LoadingElement />
    }
    return <LoadingElement />
  }

  // If no session, show loading
  if (!session) {
    return <LoadingElement />
  }

  return (
    <BrowserRouter>
      <Navigation />
      <Routes>
        <Route path="/" element={<DashboardRoute session={session} getToken={getToken} />} />
        <Route path="/order-card" element={<OrderCardRoute session={session} getToken={getToken} />} />
        <Route path="/audience-alert" element={<AudienceAlertRoute session={session} getToken={getToken} />} />
      </Routes>
    </BrowserRouter>
  )
}

// Dashboard route - raw components for standalone (no duplicate auth)
const DashboardRoute = ({ session, getToken }: { session: AppSession; getToken: () => Promise<string | null> }) => {
  return (
    <FlightDeckProvider session={session} isLoaded={true} isSignedIn={true} getToken={getToken}>
      <AdvertiserAccessGate session={session} fallback={<LoadingElement />}>
        <DashboardWrapper {...dashboardProps} />
      </AdvertiserAccessGate>
    </FlightDeckProvider>
  )
}

const OrderCardRoute = ({ session, getToken }: { session: AppSession; getToken: () => Promise<string | null> }) => {
  const [searchParams] = useSearchParams()
  const campaignGroupIdParam = searchParams.get('campaignGroupId')
  const campaignGroupId = campaignGroupIdParam ? Number(campaignGroupIdParam) : null

  if (!campaignGroupId) {
    return (
      <div className="p-8 text-header-inverse">
        <h1 className="text-2xl font-bold mb-4 text-header-inverse">Order Card Component</h1>
        <p className="mb-4 text-secondary">Add a campaign group ID to the url to test this component:</p>
        <code className="bg-background text-header p-2 block my-4 rounded border border-subtle">
          ?campaignGroupId=YOUR_ID
        </code>
        <p className="mb-2 text-secondary">
          <strong>Example:</strong>
        </p>
        <code className="bg-background text-header p-2 block my-4 rounded border border-subtle">
          /order-card?campaignGroupId=123&compact=true
        </code>
      </div>
    )
  }

  return (
    <div className="p-8 min-h-screen bg-background">
      <div className="overflow-hidden p-8">
        <FlightDeckProvider session={session} isLoaded={true} isSignedIn={true} getToken={getToken}>
          <div className="max-w-md">
            <OrderCard campaignGroupId={campaignGroupId} />
            <OrderCard campaignGroupId={campaignGroupId} theme="slate" isInverse={true} />
          </div>
          <OrderCard campaignGroupId={campaignGroupId} layout="compact" />
          <OrderCard campaignGroupId={campaignGroupId} appliedAudience={true} layout="row" />
        </FlightDeckProvider>
      </div>
    </div>
  )
}

const AudienceAlertRoute = ({ session, getToken }: { session: AppSession; getToken: () => Promise<string | null> }) => {
  const [searchParams] = useSearchParams()
  const appliedAudienceIdParam = searchParams.get('appliedAudienceId')
  const campaignIdParam = searchParams.get('campaignId')

  const appliedAudienceId = appliedAudienceIdParam ? Number(appliedAudienceIdParam) : undefined
  const campaignId = campaignIdParam ? Number(campaignIdParam) : undefined

  return (
    <div className="p-8 min-h-screen bg-surface-bg">
      <div className="max-w-2xl mx-auto bg-background rounded-lg border border-subtle shadow-md overflow-hidden min-h-48">
        <div className="p-4 border-b border">
          <h2 className="m-0 text-header text-lg font-semibold">Audience Alert Component</h2>
          <p className="mt-2 mb-0 text-secondary text-sm">
            This component shows warnings when audience size may cause underdelivery.
          </p>
        </div>

        <FlightDeckProvider session={session} isLoaded={true} isSignedIn={true} getToken={getToken}>
          <FlightDeckAudienceAlert appliedAudienceId={appliedAudienceId} campaignId={campaignId} />
        </FlightDeckProvider>

        <div className="p-4 border-t border bg-sub-surface-bg text-secondary">
          <p className="m-0 text-sm">
            <strong>Usage:</strong> Add query parameters to test the component:
          </p>
          <code className="bg-background text-header p-2 block my-2 text-xs rounded border border-subtle">
            ?appliedAudienceId=123&campaignId=456
          </code>
          <p className="mt-2 mb-0 text-xs">
            <strong>Note:</strong> Component returns null if props are missing or alert shouldn't show.
          </p>
        </div>
      </div>
    </div>
  )
}

// biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
